import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { addFavourite, listFavourites, removeFavourite } from "@/lib/api";
import { useAuth } from "@/contexts/AuthProvider";
import { toast } from "sonner";

const FavCtx = createContext({ ids: [], items: [], toggle: async () => {}, has: () => false });

export const FavouritesProvider = ({ children }) => {
  const { user } = useAuth();
  const [ids, setIds] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setIds([]);
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const data = await listFavourites();
      setItems(data || []);
      setIds((data || []).map((d) => d.id));
    } catch (e) {
      // Surface in console so silent fetch failures stay debuggable;
      // UI degrades to an empty favourites state, which is acceptable.
      // eslint-disable-next-line no-console
      console.error("[FavouritesProvider] listFavourites failed:", e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const has = useCallback((id) => ids.includes(id), [ids]);

  const toggle = useCallback(
    async (id, name = "") => {
      if (!user) {
        toast("Sign in to save favourites");
        return false;
      }
      try {
        if (ids.includes(id)) {
          const res = await removeFavourite(id);
          setIds(res.favourites);
          await refresh();
          toast(`Removed ${name || "item"} from favourites`);
          return false;
        }
        const res = await addFavourite(id);
        setIds(res.favourites);
        await refresh();
        toast.success(`Saved ${name || "item"} to favourites`);
        return true;
      } catch (e) {
        toast.error("Couldn't update favourites");
        return null;
      }
    },
    [ids, user, refresh]
  );

  return (
    <FavCtx.Provider value={{ ids, items, has, toggle, loading, refresh }}>{children}</FavCtx.Provider>
  );
};

export const useFavourites = () => useContext(FavCtx);
